import type { OwnerWorkspaceFilters } from './assetRopaFilter.types';
import { DEFAULT_OWNER_WORKSPACE_FILTERS } from './assetRopaFilter.types';
import type { OwnerWorkspaceAsset } from './assetRopaWorkspace.types';

export type OwnerWorkspaceFilterOptions = {
  assetTypes: Array<{ id: number; name: string }>;
};

const compareStrings = (left: string, right: string) => left.localeCompare(right, undefined, { sensitivity: 'base' });

export const buildOwnerWorkspaceFilterOptions = (assets: OwnerWorkspaceAsset[]): OwnerWorkspaceFilterOptions => {
  const assetTypeMap = new Map<number, string>();

  assets.forEach(asset => {
    assetTypeMap.set(asset.assetTypeId, asset.assetTypeName);
  });

  return {
    assetTypes: Array.from(assetTypeMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((left, right) => compareStrings(left.name, right.name)),
  };
};

/** Maps BE `empty_state.message_key` values to local i18n keys under `overlay_empty_state`. */
export const resolveOverlayEmptyStateMessageKey = (messageKey: string): string => {
  if (messageKey.includes('empty_processing_activities')) {
    return 'no_processing_activities';
  }

  if (messageKey.includes('awaiting_submission')) {
    return 'awaiting_submission';
  }

  const suffix = messageKey.split('.').pop() ?? messageKey;
  return suffix;
};

export const ownerWorkspaceFiltersAreActive = (filters: OwnerWorkspaceFilters): boolean =>
  JSON.stringify(filters) !== JSON.stringify(DEFAULT_OWNER_WORKSPACE_FILTERS);

export const getLinkedProcessingActivityIds = (
  assets: Array<{ assetRopaId: number; processingActivityReviews: Array<{ processingActivityId: number }> }>,
  assetRopaId: number,
): number[] => {
  const asset = assets.find(item => item.assetRopaId === assetRopaId);
  if (!asset) return [];

  return asset.processingActivityReviews.map(row => row.processingActivityId);
};

const AVAILABLE_PROCESSING_ACTIVITY_GONE_CODES = ['processing_activity_not_published', 'processing_activity_already_linked'];

export const isAvailableProcessingActivityGoneError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const response = 'response' in error ? (error as { response?: { status?: unknown; data?: unknown } }).response : undefined;
  if (response?.status !== 400 || !response.data || typeof response.data !== 'object') {
    return false;
  }

  const { code } = response.data as { code?: unknown };
  return typeof code === 'string' && AVAILABLE_PROCESSING_ACTIVITY_GONE_CODES.includes(code);
};

/** Overlay mine-detail onto the list row without wiping type/owner/updated fields omitted by the retrieve serializer. */
export const mergeOwnerWorkspaceListAssetWithDetail = (
  listAsset: OwnerWorkspaceAsset,
  detail: OwnerWorkspaceAsset,
): OwnerWorkspaceAsset => ({
  ...listAsset,
  assetRopaId: detail.assetRopaId || listAsset.assetRopaId,
  assetId: detail.assetId || listAsset.assetId,
  assetName: detail.assetName || listAsset.assetName,
  gdprRelevanceStatus: detail.gdprRelevanceStatus || listAsset.gdprRelevanceStatus,
  informationalCompleteness: detail.informationalCompleteness || listAsset.informationalCompleteness,
  processingActivityReviews: detail.processingActivityReviews,
  processingActivitiesCount: detail.processingActivityReviews.length,
  id: listAsset.id,
});
